<template>
  <div class="structure">
    <div class="structure__key" aria-label="Structure key">
      <span class="structure__key__title">Key</span>
      <div class="structure__key__items">
        <span class="structure__key__item structure__key__item--steering">Steering</span>
        <span class="structure__key__item structure__key__item--policy-team">Policy Team</span>
        <span class="structure__key__item structure__key__item--expert-groups">Tier 1 - Expert Groups</span>
        <span class="structure__key__item structure__key__item--focus-groups">Tier 1 - Focus Groups</span>
        <span class="structure__key__item structure__key__item--regular-groups">Tier 2 - Area Groups</span>
        <span class="structure__key__item structure__key__item--specialist-groups">Tier 3 - Specialist Groups</span>
      </div>
    </div>

    <div class="structure__tiers-info">
      <button
        type="button"
        class="structure__tiers-info__toggle"
        :aria-expanded="tiersInfoOpen ? 'true' : 'false'"
        aria-controls="structure-tiers-info-panel"
        @click="tiersInfoOpen = !tiersInfoOpen">
        <span class="structure__tiers-info__toggle__label">Click here for Tier descriptions and level of engagement</span>
        <BaseIcon
          tag="span"
          class="structure__tiers-info__toggle__icon"
          :type="tiersInfoOpen ? 'chevron-up' : 'chevron-down'"
        />
      </button>

      <div
        class="structure__tiers-info__panel"
        id="structure-tiers-info-panel"
        v-show="tiersInfoOpen">
        <p>Tier 1 (Focus Groups): Related to Priorities</p>
        <p>Tier 2 (Area Groups): Direct support to Tier 1 Priorities</p>
        <p>Tier 3 (Specialist Groups): Technical/Scientific &amp; Specific topics that may punctually support priorities</p>
        <p><strong>Level of engagement (subject to specific situations):</strong></p>
        <p>Tier 1: Full support = CLE manages strategy/implementation/agenda/minutes/actions tracking;</p>
        <p>Tier 2: Ad-hoc support = CLE attends on an ad-hoc/needs basis; minutes/action tracking led by Chair; and</p>
        <p>Tier 3: Self-run = Group operates independently; a short update is provided on a 90-day cycle to the relevant Policy Team and for Board pre-reads where relevant; CLE contact point remains on the distribution list.</p>
      </div>
    </div>

    <div class="structure__steering">
      <BaseTitle tag="h2" class="structure__steering__title">Steering</BaseTitle>
      <div class="structure__tabs">
        <div
          class="structure__tab"
          :class="{ 'is-active': tab.id === activeTabId }"
          :key="tab.id"
          tabindex="0"
          role="button"
          @click="selectTab(tab.id)"
          @keyup.enter="selectTab(tab.id)"
          @mouseover="hoveredTabId = tab.id"
          @mouseleave="hoveredTabId = null"
          v-for="tab in content.tabs">
          <span>{{ tab.name }}</span>
          <div class="structure__tab__actions" v-show="hoveredTabId === tab.id" v-if="tab.group">
            <router-link :to="routeGroup(tab.group)" class="structure__tab__actions__item"
              @click.native.stop
              @mouseover.native="tooltipGroupId = tab.id"
              @mouseleave.native="tooltipGroupId = null">
              <ElementTooltip
                :text="'View Group'"
                :active="tooltipGroupId === tab.id"
              />
              <BaseIcon
                type="zoom"
                :text="'View group'"
              />
            </router-link>
            <router-link :to="routeMembers(tab.group)" class="structure__tab__actions__item"
              @click.native.stop
              @mouseover.native="tooltipUsersId = tab.id"
              @mouseleave.native="tooltipUsersId = null">
              <ElementTooltip
                :text="'View members'"
                :active="tooltipUsersId === tab.id"
              />
              <BaseIcon
                type="user-filter"
                :text="'View members'"
              />
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="structure__teams" v-if="activeTab && activeTab.policyTeams">
      <ParagraphStructureTree
        :key="policyTeam.id"
        :title="policyTeam.name"
        :group="policyTeam.group"
        :root-color="policyTeam.color"
        :tree="policyTeam.tree"
        :connect-above="index > 0"
        :connect-below="index < activeTab.policyTeams.length - 1"
        v-for="(policyTeam, index) in activeTab.policyTeams"
      />
    </div>
  </div>
</template>

<script>
import BaseTitle from '@base/BaseTitle'
import BaseIcon from '@base/BaseIcon'
import ElementTooltip from '@elements/ElementTooltip'
import ParagraphStructureTree from '@blocks/paragraphs/ParagraphStructureTree'
import { groupMembersRoute } from '@modules/filters'

export default {
  name: 'ParagraphStructure',
  props: {
    content: {
      type: Object,
      required: true
    }
  },

  components: {
    BaseTitle,
    BaseIcon,
    ElementTooltip,
    ParagraphStructureTree
  },
  data () {
    return {
      activeTabId: this.content.tabs && this.content.tabs.length ? this.content.tabs[0].id : null,
      hoveredTabId: null,
      tiersInfoOpen: false,
      tooltipGroupId: null,
      tooltipUsersId: null
    }
  },
  computed: {
    activeTab () {
      return (this.content.tabs || []).find(tab => tab.id === this.activeTabId) || null
    }
  },
  methods: {
    selectTab (id) {
      this.activeTabId = id
    },
    routeGroup (group) {
      return `/contacts/groups-teams/details/${group}`
    },
    routeMembers (group) {
      return groupMembersRoute(group)
    }
  }
}
</script>

<style lang="scss">
.structure{

  &__key{
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: layout("spacing");
    margin-bottom: layout("spacing");

    &__title{
      color: colors(font);
      font-style: italic;
      font-weight: font-weights("medium");
    }

    &__items{
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: layout("spacing") / 2;
    }

    &__item{
      border: 1px solid black;
      border-radius: layout(border-radius);
      color: black;
      font-size: font-sizes("small");
      font-weight: font-weights("medium");
      padding: .5rem 1rem;
      text-align: center;

      &--steering{
        background-color: map-get($colors, travels);
      }

      &--policy-team{
        background-color: map-get($structure-colors, approval);
      }

      &--expert-groups{
        background-color: rgb(250, 152, 0);
      }

      &--focus-groups{
        background-color: rgb(157, 195, 230);
      }

      &--regular-groups{
        background-color: rgb(244, 182, 194);
      }

      &--specialist-groups{
        background-color: rgb(217, 217, 217);
      }
    }
  }

  &__tiers-info{
    margin-bottom: layout("spacing");
    text-align: right;

    &__toggle{
      align-items: center;
      background: none;
      border: none;
      color: colors(primary);
      cursor: pointer;
      display: inline-flex;
      font-size: 1.4rem;
      font-weight: font-weights("medium");
      gap: .6rem;
      padding: 0;

      &__label{
        text-decoration: underline;
      }

      &:hover &__label,
      &:focus &__label{
        text-decoration: none;
      }

      &__icon{
        font-size: 1.2rem;
      }
    }

    &__panel{
      background-color: colors(foreground);
      border-radius: layout(border-radius);
      margin-top: layout("spacing") / 2;
      padding: layout("spacing");
      text-align: left;

      p{
        font-size: 1.4rem;
        margin: 0 0 layout("spacing") / 2;

        &:last-child{
          margin-bottom: 0;
        }
      }
    }
  }

  &__steering{
    background-color: #ffc000;
    border-radius: layout(border-radius);
    padding: layout("spacing");
    margin-bottom: layout("spacing");
    text-align: center;

    &__title{
      color: black;
      margin-bottom: layout("spacing")/2;
    }
  }

  &__tabs{
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: layout("spacing")/2;
  }

  &__tab{
    position: relative;
    background-color: rgba(white, .2);
    border: 1px solid transparent;
    border-radius: layout(border-radius);
    color: black;
    padding: .6rem 1.2rem;
    font-weight: font-weights("medium");
    cursor: pointer;

    @include hover{
      background-color: rgba(white, .3);
    }

    &.is-active{
      background-color: white;
      color: black;
    }

    &__actions{
      display: flex;
      position: absolute;
      bottom: -1.9rem;
      right: 0;
      background-color: white;
      border-radius: layout(border-radius);
      box-shadow: 0 0 0 1px tint(black, 90%);
      padding: .3rem .5rem;
      z-index: 10;

      .icon:before{
        color: colors(primary);
        font-size: 1.4rem;
      }

      &__item{
        color: colors(primary);
        position: relative;

        &:not(:last-child){
          margin-right: .8rem;
        }

        &:hover{
          color: colors(primary);
        }

        .tooltip{
          white-space: nowrap;
        }
      }
    }
  }

  &__teams{
    display: flex;
    flex-direction: column;
  }
}
</style>
